
import Image from "./Image";
import { useInshurLanding } from "../landingMode";
import { ArrowRight, ScanFace, BadgeCheck, ShieldCheck, Car, FileCheck } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { RotatingChecks } from "./RotatingChecks";
import { SpeakCta, ASSESSMENT_URL } from "./SpeakCta";
/* AI-channel verification panel, styled after the Shopify "entered the chat"
   card but recoloured with the RentID cyan-blue: a dark radial gradient glowing
   from the top-left corner down to near-black.

   The whole card links to RentID via a stretched-link overlay, which lets the
   video chip act as an independent control that opens a video pop-up.

   Ported from the v0 branch's 2026-09-27/28 revision (v0/update-hero-sub-copy): copy in a
   left column, and beside it a scattered collage where the video player anchors the lower
   middle with compact glass verification cards and three lifestyle photos floating around it
   at staggered heights. Smaller screens fall back to the player plus a tidy card list. */
// The verification checks surfaced as glass cards, mirroring the rotating
// subcopy beneath the heading. Each pairs a label with a representative icon.
const VERIFY_CHECKS = [
    { label: "ID & Biometrics Check", icon: ScanFace },
    { label: "Active / Suspended License", icon: BadgeCheck },
    { label: "Criminal Record Check", icon: ShieldCheck },
    { label: "Motor Vehicle Reports", icon: Car },
    { label: "Driver Insurance Validation", icon: FileCheck },
];
// Desktop collage layout: each tile's absolute position within the stage.
// Tiles sit beneath the player (z-10), so ones overlapping its edges tuck behind.
// Photos were converted from the v0 repo's 1.5MB PNGs to 640px WebP (35-50KB each).
const COLLAGE = [
    { kind: "photo", src: "/images/verify-keys-handoff.webp", alt: "Car keys handed to a verified renter", pos: "left-0 top-[4%]" },
    { kind: "card", check: VERIFY_CHECKS[0], pos: "left-[33%] top-[10%]" },
    { kind: "photo", src: "/images/verify-selfie-id.webp", alt: "Driver taking a verification selfie in a car", pos: "left-[62%] top-0" },
    { kind: "card", check: VERIFY_CHECKS[1], pos: "right-0 top-[20%]" },
    { kind: "card", check: VERIFY_CHECKS[2], pos: "left-0 top-[42%]" },
    { kind: "card", check: VERIFY_CHECKS[3], pos: "right-0 top-[48%]" },
    { kind: "photo", src: "/images/verify-license-scan.webp", alt: "Driver scanning a license with a phone", pos: "left-[5%] top-[78%]" },
    { kind: "card", check: VERIFY_CHECKS[4], pos: "right-[9%] top-[80%]" },
];
/* Collage as shown (owner, 2026-09-30, first for /inshur, then for the home page too):
   starting from v0's COLLAGE, the "Motor Vehicle Reports" card swapped places with the license-scan
   photo (bottom-left of the player), then all three photo tiles were removed. So: the five cards
   only, with Motor Vehicle Reports sitting where the license photo was. COLLAGE above is kept as
   the v0 reference. */
const SHOWN_COLLAGE = COLLAGE.filter((t) => t.kind === "card").map((t) => (t.check === VERIFY_CHECKS[3] ? { ...t, pos: "left-[5%] top-[78%]" } : t));
/* The RentID product video from rent-id.io's "Pre-rental verification tools" section. It is a
   Vimeo video ("RentID", 60s, the rentid Vimeo account), embedded there with these exact player
   options; Vimeo offers no downloadable file, so it is embedded here too. Owner (2026-09-30): it
   must NOT autoplay. At rest the player shows the video's poster with a play button; the click
   mounts the Vimeo player (autoplay with sound, since the user asked for it, looping, Vimeo's
   controls) and lifts the player above the collage cards that overlap it. */
const RENTID_VIMEO_SRC = "https://player.vimeo.com/video/1185610303?badge=0&autopause=0&player_id=0&app_id=58479&autoplay=1&loop=1&controls=1";
/* Home page (owner, 2026-10-01, superseding the 09-30 rule there): the video autoplays, muted
   (browsers block audible autoplay), looping, with controls so it can be unmuted, and the player
   always sits above the collage cards. The player mounts once the section is near the viewport.
   /inshur keeps the click-to-play behaviour above. */
const RENTID_VIMEO_AUTOPLAY_SRC = "https://player.vimeo.com/video/1185610303?badge=0&autopause=0&player_id=0&app_id=58479&autoplay=1&muted=1&loop=1&controls=1";
const RENTID_VIMEO_POSTER = "/images/home/rentid-video-poster.webp"; // the video's own Vimeo thumbnail
// A single frosted-glass verification card: icon tile above, label below, held
// to a square footprint so the set reads as a tidy grid of tiles.
function CheckCard({ icon: Icon, label, className = "", compact = false }) {
    return (<div className={`flex aspect-square flex-col justify-between border border-white/10 bg-white/[0.06] shadow-[0_16px_40px_-24px_rgba(0,0,0,0.85)] backdrop-blur-md ${compact ? "rounded-xl p-2.5" : "rounded-2xl p-4"} ${className}`}>
      <span className={`flex items-center justify-center bg-white/10 ring-1 ring-white/15 ${compact ? "h-7 w-7 rounded-lg" : "h-11 w-11 rounded-xl"}`}>
        <Icon aria-hidden="true" className={`text-white ${compact ? "h-3.5 w-3.5" : "h-5 w-5"}`}/>
      </span>
      <span className={`font-sans font-medium text-white text-pretty ${compact ? "text-[11px] leading-tight" : "text-sm leading-snug"}`}>
        {label}
      </span>
    </div>);
}
/* Props (v0 2026-10-13, for /tnt-group): hideLearnMore drops the "Learn more" line (defaults to the
   INSHUR landing mode), wide uses the rentShield card's gutters, spacious tops the copy, logoClassName
   sizes the lockup and ctaLabel adds a green "Start for Free" style button under the copy. */
export function HomeAgentic({ hideLearnMore, wide = false, logoClassName = "h-16 sm:h-20", spacious = false, ctaLabel } = {}) {
    const inshur = useInshurLanding(); // /inshur: no links off the page
    const showLearnMore = !(hideLearnMore ?? inshur);
    // INSHUR page: the embedded player is mounted only after a click. Home page: it autoplays
    // (muted) once the section is near the viewport, and is always lifted above the cards.
    const autoplay = !inshur;
    const [playing, setPlaying] = useState(false);
    const [near, setNear] = useState(false);
    const stageRef = useRef(null);
    useEffect(() => {
        if (!autoplay || !stageRef.current) return undefined;
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) { setNear(true); observer.disconnect(); }
        }, { rootMargin: "400px 0px" });
        observer.observe(stageRef.current);
        return () => observer.disconnect();
    }, [autoplay]);
    const lifted = autoplay || playing;
    return (<section className={`mx-auto w-full max-w-7xl ${wide ? "px-4 sm:px-6" : "px-5 sm:px-8 md:px-16"}`} aria-labelledby="agentic-heading">
      <div className="group relative isolate overflow-hidden rounded-2xl border-t border-white/15 transition-shadow hover:shadow-[0_24px_60px_-20px_rgba(0,0,0,0.6)] sm:rounded-3xl" style={{
            backgroundColor: "#050505",
            backgroundImage: "radial-gradient(115% 95% at 90% 88%, #22C9F0 0%, #1668A6 34%, #0A2536 68%, #050505 100%)",
        }}>
        {/* Stretched link — sits behind the content so the whole card is clickable,
            while any element marked pointer-events-auto (the video chip) stays
            independently interactive. */}
        {!inshur && (<a href="/rent-id" className="absolute inset-0 z-0">
          <span className="sr-only">Learn more about RentID driver verification</span>
        </a>)}

        {/* Content is pointer-events-none so background clicks fall through to the
            stretched link above; interactive children opt back in explicitly. */}
        <div className="pointer-events-none relative z-10 p-6 sm:p-10 lg:flex lg:items-center lg:gap-10 lg:p-12">
          {/* Brand mark on top, heading + copy below. */}
          <div className={`max-w-2xl lg:w-[40%] lg:shrink-0 ${spacious ? "lg:self-start" : ""}`}>
            <Image src="/images/rentid-logo.svg" alt="RentID" width={184} height={105} priority className={`w-auto object-contain object-left ${logoClassName}`}/>

            <div className={`mt-8 sm:mt-10 ${spacious ? "lg:mt-12" : ""}`}>
              <h2 id="agentic-heading" className="font-sans text-3xl font-normal leading-[1.05] sm:text-4xl md:text-[44px] tracking-tight text-white text-balance">
                Verify every driver
              </h2>
              <RotatingChecks />
              <p className="mt-6 max-w-md font-sans text-base leading-relaxed sm:text-lg text-white/60 text-pretty">
                Screen drivers with platform level verification tools that reduce risk prior to
                handing off the keys.
              </p>
              {showLearnMore && (
              <span className="mt-6 inline-flex items-center gap-2 align-middle text-base font-normal text-white sm:text-lg">
                <span className="underline decoration-white/40 underline-offset-4 transition-colors group-hover:decoration-white">
                  Learn more
                </span>
                <ArrowRight aria-hidden="true" className="learn-more-arrow h-5 w-5 shrink-0"/>
              </span>
              )}
              {ctaLabel ? (<div className="pointer-events-auto mt-8">
                  <SpeakCta href={ASSESSMENT_URL} label={ctaLabel}/>
                </div>) : null}
            </div>
          </div>

          {/* Stage — a scattered collage: the video player anchors the lower middle while
              glass cards and lifestyle photos float around it at varied heights, some tucked
              behind the player's edges. Smaller screens collapse to the player plus a tidy
              list below the copy. */}
          {/* Owner (2026-10-01): stage shortened from 540px so the card is less tall. */}
          <div ref={stageRef} className="relative mt-12 min-w-0 lg:mt-0 lg:h-[460px] lg:flex-1">
            {SHOWN_COLLAGE.map((tile) => tile.kind === "card"
        ? (<CheckCard key={tile.check.label} icon={tile.check.icon} label={tile.check.label} compact className={`absolute z-20 hidden w-24 lg:flex xl:w-28 ${tile.pos}`}/>)
        : (<div key={tile.src} className={`absolute hidden overflow-hidden rounded-2xl border border-white/10 shadow-[0_16px_40px_-24px_rgba(0,0,0,0.85)] lg:block ${tile.size || "aspect-square w-24 xl:w-28"} ${tile.pos}`}>
                  <Image src={tile.src} alt={tile.alt} fill sizes="128px" className="object-cover"/>
                </div>))}

            {/* z-10 keeps the stage behind the overlapping collage cards (z-20); a playing video
                lifts it to z-30, in front of them. */}
            <div className={`lg:absolute lg:inset-x-[13%] lg:top-[36%] ${lifted ? "lg:z-30" : "lg:z-10"}`}>
              {/* Horizontal video player. pointer-events-auto so it stays clickable over the
                  stretched card link (home page). */}
              <div className="flex justify-center">
                <div className={`pointer-events-auto relative aspect-video w-full max-w-xl overflow-hidden rounded-3xl border border-white/10 bg-[#0A0A0A] lg:max-w-none lg:rounded-2xl transition-shadow duration-500 ${lifted ? "shadow-[0_28px_40px_-8px_rgba(0,0,0,0.85),0_60px_110px_-10px_rgba(0,0,0,0.95)]" : "shadow-[0_24px_60px_-20px_rgba(0,0,0,0.7)]"}`}>
                  {autoplay && near ? (<iframe src={RENTID_VIMEO_AUTOPLAY_SRC} title="RentID" allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share" referrerPolicy="strict-origin-when-cross-origin" className="absolute inset-0 h-full w-full border-0"/>) : autoplay ? (<Image src={RENTID_VIMEO_POSTER} alt="" fill sizes="640px" className="object-cover"/>) : playing ? (<iframe src={RENTID_VIMEO_SRC} title="RentID" allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share" referrerPolicy="strict-origin-when-cross-origin" className="absolute inset-0 h-full w-full border-0"/>) : (<button type="button" onClick={() => setPlaying(true)} aria-label="Play the RentID video" className="group/vid absolute inset-0 flex items-center justify-center outline-none focus-visible:ring-2 focus-visible:ring-white">
                    <Image src={RENTID_VIMEO_POSTER} alt="" fill sizes="640px" className="object-cover"/>
                    <span aria-hidden="true" className="pointer-events-none absolute inset-0 bg-black/25 transition-colors group-hover/vid:bg-black/15"/>
                    <span aria-hidden="true" className="relative flex h-16 w-16 items-center justify-center rounded-full bg-white/15 ring-1 ring-white/30 backdrop-blur-sm transition-transform group-hover/vid:scale-110">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="#fff" aria-hidden="true">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    </span>
                  </button>)}
                </div>
              </div>
            </div>

            {/* Cards grid for smaller screens. */}
            <div className="mt-8 flex flex-wrap justify-center gap-3 sm:gap-4 lg:hidden">
              {VERIFY_CHECKS.map((c) => (<CheckCard key={c.label} icon={c.icon} label={c.label} className="w-full sm:w-[calc((100%-2rem)/3)] max-sm:aspect-auto max-sm:flex-row max-sm:items-center max-sm:justify-start max-sm:gap-4 max-sm:p-3"/>))}
            </div>
          </div>
        </div>
      </div>
    </section>);
}
